/**
 * TopicGenerationModal Component
 *
 * Modal for AI-powered topic generation workflow.
 * Allows users to specify count, view generated topics, select which to save.
 */

import { useMemo, useCallback, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useTopicGeneration } from '../../hooks/useTopicGeneration';
import { ITopicGenerationModalProps } from '../../@types/topics';
import { IBusinessProfileSnapshot } from '../../@types/business';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';

const TopicGenerationModal = ({ isOpen, onClose, onSuccess }: ITopicGenerationModalProps) => {
  const [topicCount, setTopicCount] = useState<string>('10');

  // Fetch business profile
  const businessProfile = useQuery(api.businesses.queries.getMyBusinessProfile, {});

  // Topic generation hook
  const {
    state,
    generateTopics,
    toggleTopic,
    selectAll,
    deselectAll,
    saveSelectedTopics,
    reset,
    hasGeneratedTopics,
    hasSelectedTopics,
    selectedTopicCount,
    isGenerating,
    isSaving,
  } = useTopicGeneration();

  // Reset state when modal closes. Store the previous isOpen as state so we can detect
  // the open→close transition during render (React's derived-state pattern — no effect needed).
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen && !isOpen) {
    setPrevIsOpen(false);
    reset();
    if (topicCount !== '10') setTopicCount('10');
  } else if (!prevIsOpen && isOpen) {
    setPrevIsOpen(true);
  }

  // Business profile snapshot for saving topics
  const profileSnapshot = useMemo<IBusinessProfileSnapshot | null>(() => {
    if (!businessProfile) {
      return null;
    }

    return {
      companyName: businessProfile.companyName,
      brandName: businessProfile.brandName,
      description: businessProfile.description,
      businessCategory: businessProfile.businessCategory,
      websiteUrl: businessProfile.websiteUrl,
    };
  }, [businessProfile]);

  // Handle generate button click
  const handleGenerate = useCallback(async () => {
    const count = parseInt(topicCount, 10);
    await generateTopics(count);
  }, [topicCount, generateTopics]);

  // Handle save button click
  const handleSave = useCallback(async () => {
    if (!profileSnapshot) {
      return;
    }

    const success = await saveSelectedTopics(profileSnapshot);

    if (success) {
      onSuccess();
      onClose();
    }
  }, [profileSnapshot, saveSelectedTopics, onSuccess, onClose]);

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!isGenerating && !isSaving) {
      onClose();
    }
  }, [isGenerating, isSaving, onClose]);

  // Topic count options (5, 10, 20)
  const countOptions = useMemo(() => {
    return [5, 10, 20];
  }, []);

  // Check if user has a business profile
  const hasProfile = businessProfile !== null && businessProfile !== undefined;
  const showNoProfileError = !hasProfile && !isGenerating;

  // Show save success message
  const showSaveSuccess = state.status === 'saved' && state.saveResult !== null;
  const savedCount = state.saveResult?.saved.length || 0;
  const duplicatesCount = state.saveResult?.duplicates.length || 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate Content Topics</DialogTitle>
          <DialogDescription>
            Use AI to generate topic ideas based on your business profile
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* No Profile Error */}
          {showNoProfileError && (
            <div className="p-4 bg-destructive/10 text-destructive rounded-md">
              <p className="font-medium">No Business Profile Found</p>
              <p className="text-sm mt-1">
                Please create a business profile first before generating topics.
              </p>
            </div>
          )}

          {/* Generation Controls */}
          {!hasGeneratedTopics && !showNoProfileError && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="topic-count" className="text-sm font-medium">
                  Number of Topics
                </label>
                <Select value={topicCount} onValueChange={setTopicCount} disabled={isGenerating}>
                  <SelectTrigger id="topic-count">
                    <SelectValue placeholder="Select count" />
                  </SelectTrigger>
                  <SelectContent>
                    {countOptions.map(count => (
                      <SelectItem key={count} value={count.toString()}>
                        {count} {count === 1 ? 'topic' : 'topics'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Generate between 1 and 20 topic suggestions
                </p>
              </div>

              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !hasProfile}
                className="w-full"
              >
                {isGenerating ? 'Generating...' : 'Generate Topics'}
              </Button>
            </div>
          )}

          {/* Error Display */}
          {state.status === 'error' && state.error && (
            <div className="p-4 bg-destructive/10 text-destructive rounded-md">
              <p className="font-medium">Error</p>
              <p className="text-sm mt-1">{state.error}</p>
              {state.errorCode === 'NO_BUSINESS_PROFILE' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => (window.location.href = '/business-settings')}
                >
                  Go to Business Settings
                </Button>
              )}
            </div>
          )}

          {/* Generated Topics */}
          {hasGeneratedTopics && (
            <div className="space-y-4">
              {/* Selection Controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{state.generatedTopics.length} topics generated</Badge>
                  <Badge variant={hasSelectedTopics ? 'default' : 'outline'}>
                    {selectedTopicCount} selected
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll} disabled={isSaving}>
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={deselectAll} disabled={isSaving}>
                    Deselect All
                  </Button>
                </div>
              </div>

              {/* Topics List */}
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {state.generatedTopics.map((topic, _index) => {
                  const isSelected = state.selectedTopicNames.has(topic.name);
                  return (
                    <div
                      key={topic.name}
                      className={`flex items-start gap-3 p-4 border rounded-lg transition-colors ${
                        isSelected ? 'bg-accent border-primary' : 'hover:bg-accent/50'
                      }`}
                    >
                      <Checkbox
                        id={`topic-${topic.name}`}
                        checked={isSelected}
                        onCheckedChange={() => toggleTopic(topic.name)}
                        disabled={isSaving}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <label
                          htmlFor={`topic-${topic.name}`}
                          className="font-medium cursor-pointer block"
                        >
                          {topic.name}
                        </label>
                        <p className="text-sm text-muted-foreground mt-1">{topic.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Save Success Message */}
              {showSaveSuccess && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-100 rounded-md">
                  <p className="font-medium">Topics Saved Successfully!</p>
                  <p className="text-sm mt-1">
                    {savedCount} {savedCount === 1 ? 'topic' : 'topics'} saved
                    {duplicatesCount > 0 &&
                      ` (${duplicatesCount} ${
                        duplicatesCount === 1 ? 'duplicate' : 'duplicates'
                      } skipped)`}
                  </p>
                  {duplicatesCount > 0 && (
                    <div className="mt-2 text-sm">
                      <p className="font-medium">Skipped duplicates:</p>
                      <ul className="list-disc list-inside mt-1">
                        {state.saveResult?.duplicates.map(name => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleClose} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={!hasSelectedTopics || isSaving}>
                  {isSaving ? 'Saving...' : `Save Selected (${selectedTopicCount})`}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { TopicGenerationModal };
